import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/config";

const BASE = SITE.siteUrl;

const physicianLd = {
  "@context": "https://schema.org",
  "@type": "Physician",
  name: "Tatiana Nakryzhskaya, MD, PhD, CFMP",
  medicalSpecialty: ["Internal Medicine", "Rheumatology", "Preventive Medicine", "Functional Medicine"],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Praha",
    addressCountry: "CZ",
  },
  availableLanguage: ["cs", "ru", "en"],
};

export default function Seo({ page, title, description, ogImage, article, withFaq, path: pathProp }) {
  const { lang, t } = useLanguage();
  const path = pathProp !== undefined ? pathProp : page === "home" ? "" : `/${page}`;
  const metaTitle = title || t.seo[page]?.title;
  const metaDesc = description || t.seo[page]?.desc;
  const url = `${BASE}/${lang}${path}`;
  const image = ogImage || `${BASE}/images/doctor-hero.png`;

  const jsonLds = [physicianLd];
  if (article) {
    jsonLds.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description: metaDesc,
      image,
      datePublished: article.datePublished,
      inLanguage: lang,
      author: {
        "@type": "Person",
        name: "Tatiana Nakryzhskaya, MD, PhD, CFMP",
        jobTitle: "Preventive & Functional Medicine Physician",
      },
      publisher: { "@type": "Person", name: "Tatiana Nakryzhskaya, MD, PhD, CFMP" },
    });
  }
  if (withFaq && t.faq?.items) {
    jsonLds.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faq.items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={url} />
      {["cs", "ru", "en"].map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${BASE}/${l}${path}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE}/cs${path}`} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === "cs" ? "cs_CZ" : lang === "ru" ? "ru_RU" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={image} />
      {jsonLds.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
